/**
 * HIYAZAKURA — cold-reactive sakazuki, photo-derived
 * ==================================================
 *
 * Earlier builds drew the cup procedurally. They never looked like the real
 * object, so this one doesn't draw it at all. It uses the real cup:
 *
 *   dish_bloom.webp  the actual frame from the reference footage, full bloom,
 *                    alpha-masked to the dish
 *   dish_bare.webp   the same frame with the thermochromic pink removed — the
 *                    warm state. Same lighting, same glaze, pixel-aligned.
 *   dish_vivid.webp  pink deepened, for the coldest pours
 *
 * Because the tree is printed permanently and only the pink answers the cold,
 * "bloom" is a reveal: the bloomed frame shows through an irregular front that
 * grows outward from the trunk, exactly as the footage shows. Nothing is
 * painted; what you see is the photograph.
 *
 * Assets are derived by tools/derive-dish.py from the reference frame; the
 * trunk origin and canopy extents below are measured from that derivation.
 *
 * API:  createBlossomCup(canvas, opts) -> { setChill, getChill, invalidate, destroy }
 *       opts: { reducedMotion, onReady, onError, assets: { bare, bloom, vivid } }
 */

const IMG_W = 1073, IMG_H = 796;                    // derived image size (tools/derive-dish.py)
const TRUNK = { x: 0.495, y: 0.790 };              // trunk base, normalised
const ORIGIN = { x: 0.495, y: 0.721 };             // where the cold reaches first
const CANOPY_R = 0.382;                            // reach needed to cover the canopy, × IMG_W
const ELLIPSE_Y = 0.62;                            // canopy is wider than tall
const FRONT_W = 0.17;                              // feather of the reveal front
const REACH = 1.45;                                // chill=1 must fully cover the canopy corners (373px vs R=341px)

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (a, b, t) => { const x = clamp01((t - a) / (b - a)); return x * x * (3 - 2 * x); };

function assetUrl(name) {
  // On Shopify this module is served as `<prefix>blossom.js?v=…` from the flat
  // assets folder, and the dish images carry the same prefix. Derive it from our
  // own filename so the images resolve without any page-level configuration.
  // (A template-literal path cannot be rewritten by tools/asset-versions.mjs.)
  try {
    const self = new URL(import.meta.url);
    const file = self.pathname.split('/').pop() || '';
    const at = file.indexOf('blossom.js');
    const prefix = at > 0 ? file.slice(0, at) : '';
    return new URL('./' + prefix + name, self).href;
  } catch { return name; }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.decoding = 'async';
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error(`image failed: ${src}`));
    im.src = src;
  });
}

/* Tileable value noise used to roughen the bloom front. White, alpha = noise. */
function makeNoise(size, seed) {
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const g = cv.getContext('2d');
  const id = g.createImageData(size, size);
  let s = seed >>> 0;
  const rnd = () => { s = (s + 0x6D2B79F5) | 0; let t = Math.imul(s ^ (s >>> 15), 1 | s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const octave = (cells) => {
    const grid = new Float32Array(cells * cells);
    for (let i = 0; i < grid.length; i++) grid[i] = rnd();
    const out = new Float32Array(size * size);
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
      const gx = (x / size) * cells, gy = (y / size) * cells;
      const x0 = Math.floor(gx), y0 = Math.floor(gy);
      const fx = gx - x0, fy = gy - y0;
      const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
      const v = (xx, yy) => grid[((yy % cells) * cells) + (xx % cells)];
      const a = v(x0, y0), b = v(x0 + 1, y0), c = v(x0, y0 + 1), d = v(x0 + 1, y0 + 1);
      out[y * size + x] = (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
    }
    return out;
  };
  const o1 = octave(4), o2 = octave(9), o3 = octave(19);
  for (let i = 0; i < size * size; i++) {
    const n = 0.55 * o1[i] + 0.30 * o2[i] + 0.15 * o3[i];
    const k = i * 4;
    id.data[k] = id.data[k + 1] = id.data[k + 2] = 255;
    id.data[k + 3] = Math.round(255 * (0.30 + 0.70 * n));
  }
  g.putImageData(id, 0, 0);
  return cv;
}

export function createBlossomCup(canvas, opts = {}) {
  const onError = typeof opts.onError === 'function' ? opts.onError : null;
  const fail = (err) => { if (onError) { try { onError(err); } catch { /* theirs */ } } else { throw err; } };

  try {
    if (!canvas || canvas.tagName !== 'CANVAS') throw new TypeError('createBlossomCup: expected a <canvas>');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D context unavailable');

    const coarse = matchMedia('(pointer: coarse)').matches;
    const mqReduce = matchMedia('(prefers-reduced-motion: reduce)');
    let reduced = opts.reducedMotion === true || mqReduce.matches;
    const maxDpr = coarse ? 1.25 : 2;
    const frameMs = coarse ? 1000 / 30 : 1000 / 60;

    const assets = Object.assign({
      bare: assetUrl('dish_bare.webp'),
      bloom: assetUrl('dish_bloom.webp'),
      vivid: assetUrl('dish_vivid.webp'),
    }, opts.assets || {});

    /* ---------------- state ---------------- */
    let img = null;                       // { bare, bloom, vivid }
    let chill = 0, targetChill = 0;
    let dpr = 1, cssW = 0, cssH = 0;
    let box = { x: 0, y: 0, w: 0, h: 0 }; // where the dish image sits, css px
    let staticLayer = null, maskCv = null, revCv = null, noiseCv = null, noisePat = null;
    let raf = 0, last = 0, settled = true, visible = true, dead = false, ready = false;

    /* ---------------- layout ---------------- */
    function layout() {
      const rect = canvas.getBoundingClientRect();
      cssW = Math.max(1, rect.width || canvas.clientWidth || 300);
      cssH = Math.max(1, rect.height || canvas.clientHeight || 300);
      dpr = Math.min(window.devicePixelRatio || 1, maxDpr);
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);

      // Contain the dish with breathing room, sitting slightly high to leave
      // space for its shadow below.
      const pad = 0.025;
      const availW = cssW * (1 - pad * 2), availH = cssH * (1 - pad * 2);
      const s = Math.min(availW / IMG_W, availH / IMG_H);
      box.w = IMG_W * s; box.h = IMG_H * s;
      box.x = (cssW - box.w) / 2;
      box.y = (cssH - box.h) / 2 - cssH * 0.02;

      const pw = Math.max(1, Math.round(box.w * dpr)), ph = Math.max(1, Math.round(box.h * dpr));
      for (const key of ['mask', 'rev']) {
        const cv = document.createElement('canvas');
        cv.width = pw; cv.height = ph;
        if (key === 'mask') maskCv = cv; else revCv = cv;
      }
      if (!noiseCv) noiseCv = makeNoise(256, 20260919);
      noisePat = null;

      staticLayer = paintStatic();
      settled = false;
      schedule();
    }

    /* Shadow + the bare dish. Never changes, so it's painted once. */
    function paintStatic() {
      const cv = document.createElement('canvas');
      cv.width = canvas.width; cv.height = canvas.height;
      const g = cv.getContext('2d');
      if (!g) return null;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Contact shadow on the page surface.
      const cx = box.x + box.w * 0.50, cy = box.y + box.h * 0.585;
      const rx = box.w * 0.44, ry = box.h * 0.40;
      g.save();
      g.translate(cx + box.w * 0.02, cy + box.h * 0.09);
      const sh = g.createRadialGradient(0, 0, ry * 0.2, 0, 0, ry * 1.25);
      sh.addColorStop(0, 'rgba(0,0,0,0.55)');
      sh.addColorStop(0.6, 'rgba(0,0,0,0.28)');
      sh.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = sh;
      g.scale(rx / ry, 1);
      g.beginPath(); g.arc(0, 0, ry * 1.25, 0, Math.PI * 2); g.fill();
      g.restore();

      if (img) g.drawImage(img.bare, box.x, box.y, box.w, box.h);
      return cv;
    }

    /* ---------------- reveal mask ---------------- */
    function paintMask(t) {
      const g = maskCv.getContext('2d');
      const W = maskCv.width, H = maskCv.height;
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.globalCompositeOperation = 'source-over';
      g.clearRect(0, 0, W, H);
      if (t <= 0) return;

      const ox = ORIGIN.x * W, oy = ORIGIN.y * H;
      const R = CANOPY_R * W;
      const rIn = R * (t * REACH - FRONT_W);
      const rOut = R * (t * REACH + FRONT_W);

      // Elliptical space: squash y about the origin so the front is canopy-shaped.
      g.save();
      g.translate(ox, oy);
      g.scale(1, ELLIPSE_Y);
      g.translate(-ox, -oy);

      // Ring: alpha ramps 1→0 across the front, then roughened by noise.
      if (rOut > 0) {
        const ring = g.createRadialGradient(ox, oy, Math.max(0, rIn), ox, oy, rOut);
        ring.addColorStop(0, 'rgba(255,255,255,1)');
        ring.addColorStop(1, 'rgba(255,255,255,0)');
        g.fillStyle = ring;
        g.fillRect(-W, -H, W * 3, H * 3);
      }
      g.restore();

      if (!noisePat) {
        noisePat = g.createPattern(noiseCv, 'repeat');
      }
      g.globalCompositeOperation = 'destination-in';
      g.save();
      const ns = (W / 256) * 0.55;      // noise feature size ≈ 1/1.8 of dish width
      g.scale(ns, ns);
      g.fillStyle = noisePat;
      g.fillRect(0, 0, W / ns, H / ns);
      g.restore();

      // Solid, feathered core so the interior is fully revealed, not mottled.
      if (rIn > 0) {
        g.globalCompositeOperation = 'source-over';
        g.save();
        g.translate(ox, oy); g.scale(1, ELLIPSE_Y); g.translate(-ox, -oy);
        const core = g.createRadialGradient(ox, oy, rIn * 0.72, ox, oy, rIn * 1.02);
        core.addColorStop(0, 'rgba(255,255,255,1)');
        core.addColorStop(1, 'rgba(255,255,255,0)');
        g.fillStyle = core;
        g.fillRect(-W, -H, W * 3, H * 3);
        g.restore();
      }
      g.globalCompositeOperation = 'source-over';
    }

    /* ---------------- frame ---------------- */
    function render() {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (staticLayer) ctx.drawImage(staticLayer, 0, 0);
      if (!img) return;

      const t = reduced ? (chill > 0.5 ? 1 : chill > 0 ? clamp01(chill) : 0) : chill;
      if (t <= 0.002) return;

      // Colder pours read deeper: crossfade bloom → vivid over the last stretch.
      const g = revCv.getContext('2d');
      const W = revCv.width, H = revCv.height;
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.globalCompositeOperation = 'source-over';
      g.globalAlpha = 1;
      g.clearRect(0, 0, W, H);
      g.drawImage(img.bloom, 0, 0, W, H);
      const viv = smooth(0.55, 1, t);
      if (viv > 0) { g.globalAlpha = viv; g.drawImage(img.vivid, 0, 0, W, H); g.globalAlpha = 1; }

      paintMask(t);
      g.globalCompositeOperation = 'destination-in';
      g.drawImage(maskCv, 0, 0);
      g.globalCompositeOperation = 'source-over';

      // The first blush is faint; it saturates as the cold takes hold.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalAlpha = 0.35 + 0.65 * smooth(0, 0.30, t);
      ctx.drawImage(revCv, box.x, box.y, box.w, box.h);
      ctx.globalAlpha = 1;
    }

    /* ---------------- loop ---------------- */
    function tick(now) {
      raf = 0;
      if (dead) return;
      if (now - last < frameMs) { schedule(); return; }
      last = now;
      const diff = targetChill - chill;
      if (Math.abs(diff) < 0.0015) { chill = targetChill; settled = true; }
      else chill += diff * (reduced ? 1 : 0.12);
      render();
      if (!settled) schedule();
    }
    function schedule() {
      if (dead || !visible || raf) return;
      raf = requestAnimationFrame(tick);
    }

    /* ---------------- observers ---------------- */
    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver((es) => {
          visible = es.some((e) => e.isIntersecting);
          if (visible) { settled = false; schedule(); }
          else if (raf) { cancelAnimationFrame(raf); raf = 0; }
        }, { threshold: 0.01 })
      : null;
    if (io) io.observe(canvas);
    const ro = 'ResizeObserver' in window ? new ResizeObserver(() => layout()) : null;
    if (ro) ro.observe(canvas);
    const onResize = () => layout();
    if (!ro) window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });
    const onMotion = (e) => { reduced = opts.reducedMotion === true || e.matches; settled = false; schedule(); };
    if (mqReduce.addEventListener) mqReduce.addEventListener('change', onMotion);

    /* ---------------- boot ---------------- */
    layout();
    render();
    Promise.all([loadImage(assets.bare), loadImage(assets.bloom), loadImage(assets.vivid)])
      .then(([bare, bloom, vivid]) => {
        if (dead) return;
        img = { bare, bloom, vivid };
        ready = true;
        staticLayer = paintStatic();
        settled = false;
        render();
        schedule();
        if (typeof opts.onReady === 'function') { try { opts.onReady(); } catch { /* theirs */ } }
      })
      .catch((err) => { if (!dead) fail(err); });

    /* ---------------- API ---------------- */
    return {
      setChill(v) {
        const t = clamp01(Number(v) || 0);
        if (t === targetChill) return;
        targetChill = t; settled = false; schedule();
      },
      getChill() { return chill; },
      invalidate() {
        staticLayer = paintStatic();
        settled = false;
        if (visible) { render(); schedule(); }
      },
      destroy() {
        dead = true;
        if (raf) cancelAnimationFrame(raf); raf = 0;
        if (io) io.disconnect();
        if (ro) ro.disconnect();
        if (!ro) window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onResize);
        if (mqReduce.removeEventListener) mqReduce.removeEventListener('change', onMotion);
        img = null; staticLayer = maskCv = revCv = noiseCv = noisePat = null;
        try { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, canvas.width, canvas.height); } catch { /* gone */ }
      },
      get ready() { return ready; },
    };
  } catch (err) {
    fail(err);
    return { setChill() {}, getChill() { return 0; }, invalidate() {}, destroy() {}, ready: false };
  }
}

export default createBlossomCup;
